import { getDrill } from './mutations'

const initPage = function ({ commit, state, rootState }, { params }) {
    commit('setRouteInfo', params)

    // always make sure we start with clean slate, this makes it so that mounted() doesn't trigger when doing back/forward on Drills so we can use that to CYA for auto selecting
    commit('clearControls')

    return new Promise((resolve, reject) => {
        // Start product api timer
        this.$timing.start('product-api')

        getProductLineData(this.$axios, this.$config, state.slug, rootState.permId)
            .then((obj) => {
                // End timer when we get data
                this.$timing.end('product-api')
                commit('initProductLine', obj.data)
                resolve(true)
            })
            .catch((err) => {
                if (!err.response) {
                    console.error('no error response found')
                    console.error(err)
                    if (this.$log) this.$log.error(err)
                } else {
                    if (this.$log) this.$log.error(err.response.data)
                    if (err.response.status === 404) {
                        this.$axiosError()
                        resolve()
                    }
                }

                // TODO: Clean up how the error page display's errors, Maybe based upon status code.
                // eslint-disable-next-line prefer-promise-reject-errors
                reject({
                    statusCode: 500,
                    message: 'There was a problem loading this page. Please refresh to try again.',
                })
            })
    })
}

const initProductLineControls = function ({ dispatch, state, rootState, commit }) {
    return new Promise((resolve) => {
        getControlData(this.$axios, this.$config, state.slug, {})
            .then((obj) => {
                commit('initControls', { data: obj.data, fitment: rootState.fitment.data })
                dispatch('checkFitment')
                resolve()
            })
            .catch((err) => {
                // oc api returned 404, item is out ot stock, don't show OC blow up message
                if (
                    err?.response?.data?.errors.length &&
                    err?.response?.data?.errors?.[0]?.code === 404 &&
                    state?.data?.isOutOfStock
                ) {
                    resolve()
                    return
                }

                this.$uiEvents.$emit('selectItemError', {
                    type: err.response ? 'UNKNOWN' : 'NETWORK_ERROR',
                    error: err,
                })

                commit('setHasOrderControlError', { value: true })
                resolve()
            })
    })
}

const checkFitment = function ({ rootState, state, commit }) {
    const obj = {}

    // save an api hit, we don't even have any fitment to check against
    if (!rootState.fitment.data.year) return

    // put together request with only fitment stuffs
    state.controls.drillConfig.fitment.forEach((drillType) => {
        if (rootState.fitment.data[drillType]) obj[drillType] = rootState.fitment.data[drillType]
    })

    getControlData(this.$axios, this.$config, state.slug, obj)
        .then((resp) => {
            commit('setIsGoodVehicleSoFar', true)

            // we have fitment in our session for the last fitment drill of this pline (and it fits!)
            if (obj[state.controls.drillConfig.lastFitment]) {
                // fill in all fitment drills with values from your session so lower controls can load/cascade like normal
                commit('setupShortCutFitments', rootState.fitment.data)

                // skip ahead of the line
                commit('nextDrillAction', {
                    data: resp.data,
                    fitment: rootState.fitment.data,
                })
            }
        })
        .catch((axiosData) => {
            commit('setIsGoodVehicleSoFar', false)
        })
}

const setFitmentFromDrills = function ({ dispatch, commit, state, rootState }) {
    const currentFitment = rootState.fitment.data
    const newFitment = {}

    state.controls.drillConfig.fitment.forEach((drillType) => {
        const drill = getDrill(state.controls, drillType)

        const selectedOption = drill.options.find((obj) => {
            // for old cookies values that are not INTs
            // eslint-disable-next-line eqeqeq
            return obj.value == drill.currentValue
        })

        if (!selectedOption)
            this.$log.error('DRILL_VALUE_NOT_FOUND', {
                drillType,
                drillCurrentValue: drill.currentValue,
                drillOptions: drill.options,
            })

        if (drillType !== 'year')
            newFitment[`${drillType}`.replace('Slug', '')] =
                selectedOption.value !== 'n-a' ? selectedOption.text : ''

        newFitment[drillType] = selectedOption.value
    })

    // if their YMM changed, clear out any extra info they may have in their session from their previous fitment as we don't know for sure they are valid anymore!
    const merge =
        currentFitment.year === newFitment.year &&
        currentFitment.make === newFitment.make &&
        currentFitment.model === newFitment.model

    return new Promise(function (resolve) {
        commit('fitment/setFitmentData', { fitmentData: newFitment, merge }, { root: true })
        commit('setIsGoodVehicleSoFar', true)
        dispatch('fitment/initSelects', null, { root: true }) // now update the top YMM modal!
        resolve()
    })
}

// TODO: Back/Forward is more fragile than I would like.  I assume at some point we'd be loading state from history or something vs having to re-construct the whole freaking thing again and fill drills
const fillNextDrill = function (
    { commit, state, rootState },
    { sourceDrillType, newValue, groupValue, hasBadAutoSelect }
) {
    const drillConfig = state.controls.drillConfig

    commit('setSelectedValue', { sourceDrillType, newValue, groupValue })

    // If an AutoSelect wasn't able to be selected then we need to let the current OC configuration know so we can stop future autoselections
    if (hasBadAutoSelect) {
        commit('setHadBadAutoSelect', true)
        return
    }

    // stop the cascade, everything below this should already be filled in if shortcut is enabled
    if (state.controls.isShortCutFitment && sourceDrillType === drillConfig.lastFitment) {
        // let things cascade again since we are done
        commit('setIsShortCutFitment', false)
        return
    }

    const values = getSelectedValues(state.controls, sourceDrillType, groupValue)
    const expected = getExpected(sourceDrillType, drillConfig)

    getControlData(this.$axios, this.$config, state.slug, values)
        .then((obj) => {
            const received = getReceived(obj.data)

            // this doesn't check the drills that are given when we first get a group.  Since we'd have to move all this into a mutation (since we have to let the group get created to set it's error flag, meh for now)
            if (expected !== received) {
                commit('setHasOrderControlError', { groupValue, value: true })

                this.$uiEvents.$emit('selectItemError', {
                    type: 'UNEXPECTED_RESULT',
                    extra: {
                        expected,
                        received,
                        values: getPrettyDrillValues(values),
                    },
                })

                return
            }

            commit('nextDrillAction', {
                data: obj.data,
                groupValue,
                fitment: rootState.fitment.data,
            })
        })
        .catch((err) => {
            commit('setHasOrderControlError', { groupValue, value: true })

            // don't log crappy internet to GA
            if (!err.response) {
                this.$uiEvents.$emit('selectItemError', { type: 'NETWORK_ERROR', error: err })
                return
            }

            this.$uiEvents.$emit('selectItemError', {
                type: 'UNKNOWN',
                error: err,
                extra: {
                    expected,
                    received: err.response.status,
                    values: getPrettyDrillValues(values),
                },
            })
        })
}

function getPrettyDrillValues(values) {
    return JSON.stringify(values).replace(/(\{|\}|\\|")/g, '')
}

function getExpected(sourceDrillType, drillConfig) {
    const sourceIndex = drillConfig.all.indexOf(sourceDrillType)

    if (sourceIndex < drillConfig.all.length - 1) {
        const nextDrill = drillConfig.all[sourceIndex + 1]
        if (nextDrill === drillConfig.splitAt) return 'groups'

        return nextDrill
    }

    return 'product'
}

function getReceived(dataObj) {
    const arr = [] // yes an array just in case the OC response is wickety-wack
    if (dataObj.groups) arr.push('groups')
    if (dataObj.drill) arr.push(dataObj.drill.type)
    if (dataObj.product) arr.push(`product`)
    if (Object.keys(dataObj).length === 0) arr.push('nothing')
    return arr.join(', ')
}

// reset controls 2 below target, but only upon real user action
const resetControls = function ({ commit }, { sourceDrillType, groupValue }) {
    commit('setHasOrderControlError', { groupValue, value: false })
    commit('resetControls', { sourceDrillType, groupValue })
}

export default {
    initPage,
    initProductLineControls,
    fillNextDrill,
    setFitmentFromDrills,
    checkFitment,
    resetControls,
}

function getControlData(axios, config, slug, selectedValues) {
    const isProductPage = slug.includes('/')
    const ignoreSplitters = isProductPage ? '/?ignoreSplitters=1' : ''

    return axios.post(
        `${config.platform.apis.orderControls}${getProductLineSlug(slug)}${ignoreSplitters}`,
        selectedValues,
        { progress: false }
    )
}

function getProductLineData(axios, config, slug, permId) {
    return axios.get(`${config.platform.apis.product}${slug}`, {
        headers: {
            'x-perm-id': permId,
        },
    })
}

// get the full selected values in reference to the control just triggered (sourceDrillType and above)
function getSelectedValues(controlState, sourceDrillType, groupValue) {
    const selectedValues = {}
    const drillConfig = controlState.drillConfig
    const topStopIndex = !groupValue
        ? drillConfig.top.indexOf(sourceDrillType)
        : drillConfig.top.length - 1

    for (let i = 0; i <= topStopIndex; i++) {
        const drillType = drillConfig.top[i]
        selectedValues[drillType] = getDrill(controlState, drillType).currentValue
    }

    // add in bottom values
    if (groupValue) {
        const bottomStopIndex = drillConfig.bottom.indexOf(sourceDrillType)

        selectedValues[controlState.drillConfig.splitAt] = groupValue

        for (let i = 0; i <= bottomStopIndex; i++) {
            const drillType = drillConfig.bottom[i]
            selectedValues[drillType] = getDrill(controlState, drillType, groupValue).currentValue
        }
    }

    return selectedValues
}

function getProductLineSlug(slug) {
    // already pline slug
    if (!slug.includes('/')) return slug

    // product slug, return just pline
    return slug.split('/')[0]
}
